import { RhButton, RhCard, RhDivider, RhScrollbar } from "@rhythm-ui/react";
import React, { useEffect, useState } from "react";

import { JobFilterParamType } from "@/types/job";
import { UserType } from "@/types/user";

import api from "@/services/api";

import {
  locationPreferenceOptions,
  locationPreferenceValues,
} from "@/constants/candidates";
import { jobStatusOption } from "@/constants/jobs";

import showToastMessages from "@/helpers/showToastMessages";

import { propToFormatCityLabelWithRhSelect } from "@/components/component-helper/propToFormatCityLabelWithRhSelect";
import DataSelect from "@/components/dataInput/DataSelect";
import {
  citySearchApiCall,
  clientCompanySearchApiCall,
  jobRoleSearchApiCall,
  skillSearchApiCall,
} from "@/components/dataInputApiCalls";
import FilterHeader from "@/components/forms/FilterHeader";
import MinMaxFilterInput from "@/components/forms/MinMaxFilterInput";
import { CheckboxInput, RadioInput } from "@/components/input";

interface JobFilterType {
  filterJobsOn: JobFilterParamType;
  setFilterJobsOn: React.Dispatch<React.SetStateAction<JobFilterParamType>>;
  onAllFilterClear: () => void;
}

const JobFilter = ({
  filterJobsOn,
  setFilterJobsOn,
  onAllFilterClear,
}: JobFilterType) => {
  const [recruiterList, setRecruiterList] = useState<
    { label: string; value: string }[]
  >([]);

  const getRecruiterList = async () => {
    try {
      const res = await api.auth.getUser({ new_role__name: "Recruiter" });
      const list = res.data.results;

      const formatterList = list.map((recruiter: UserType) => ({
        value: recruiter.id.toString(),
        label: recruiter.name,
      }));
      setRecruiterList(formatterList);
    } catch (error) {
      showToastMessages(error);
    }
  };

  useEffect(() => {
    getRecruiterList();
  }, []);

  useEffect(() => {
    if (filterJobsOn.work_preference_new__selected?.length === 0) {
      setFilterJobsOn((state) => {
        const { ...rest } = state;
        delete rest.work_preference_new__selected;
        delete rest.city_location_new__selected;
        return rest;
      });
    }

    if (filterJobsOn.skills__selected?.length === 0) {
      setFilterJobsOn((state) => {
        const { ...rest } = state;
        delete rest.skills__selected;
        return rest;
      });
    }

    if (filterJobsOn.company__selected?.length === 0) {
      setFilterJobsOn((state) => {
        const { ...rest } = state;
        delete rest.company__selected;
        return rest;
      });
    }
  }, [
    filterJobsOn.work_preference_new__selected,
    filterJobsOn.company__selected,
    filterJobsOn.skills__selected,
  ]);

  const handleClearFilterKey = (keys: (keyof JobFilterParamType)[]) => {
    setFilterJobsOn((state) => {
      const { ...rest } = state;
      keys.forEach((key) => {
        delete rest[key];
      });
      return rest;
    });
  };

  return (
    <>
      <RhCard className="mb-tm2 flex flex-col border">
        <div className="flex h-[48px] items-center gap-tpico border-b border-gray-100 px-tsm py-tpico">
          <h4>Filters</h4>

          <RhButton
            className={`button-tertiary button-sm ml-auto`}
            onClick={onAllFilterClear}
          >
            Clear All
          </RhButton>
        </div>

        <div className="flex-1 overflow-auto">
          <RhScrollbar style={{ height: "100%" }}>
            <div className="px-tsm py-tpico">
              <div className="flex items-center justify-between py-tnano">
                <h5>Job Status</h5>
              </div>
              <RadioInput
                values={filterJobsOn}
                setReactState={setFilterJobsOn}
                valueKey="status"
                options={[...jobStatusOption]}
              />
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <div className="flex items-center justify-between py-tnano">
                <h5>Job Scope</h5>
              </div>
              <RadioInput
                values={filterJobsOn}
                setReactState={setFilterJobsOn}
                valueKey="is_codemonk_handpick"
                options={[
                  { label: "Zelevate", value: "false" },
                  { label: "Codemonk Handpicked", value: "true" },
                ]}
              />
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <FilterHeader
                title={"Job Role"}
                handleClearFilterKey={handleClearFilterKey}
                values={filterJobsOn}
                valueKey={["job_role_details__selected"]}
              />
              <DataSelect
                apiCall={jobRoleSearchApiCall}
                values={filterJobsOn}
                valueKey={"job_role_details__selected"}
                setReactState={setFilterJobsOn}
                placeholder="Search job role"
                className="mb-tsm mt-tpico"
              />
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <FilterHeader
                title={"Company"}
                handleClearFilterKey={handleClearFilterKey}
                values={filterJobsOn}
                valueKey={["company__selected"]}
              />
              <DataSelect
                apiCall={clientCompanySearchApiCall}
                values={filterJobsOn}
                valueKey={"company__selected"}
                setReactState={setFilterJobsOn}
                isMulti
                placeholder="Search Company"
                className="mb-tsm mt-tpico"
              />
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <FilterHeader
                title={"Skills"}
                handleClearFilterKey={handleClearFilterKey}
                values={filterJobsOn}
                valueKey={["skills__selected"]}
              />

              <DataSelect
                apiCall={skillSearchApiCall}
                values={filterJobsOn}
                valueKey={"skills__selected"}
                setReactState={setFilterJobsOn}
                isMulti
                placeholder="Search Skills"
                className="mb-tsm mt-tpico"
              />
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <MinMaxFilterInput
                label="Experience(Yrs)"
                filterOn={filterJobsOn}
                setFilterOn={setFilterJobsOn}
                minKey={"min_exp"}
                maxKey="max_exp"
                handleClearFilterKey={handleClearFilterKey}
              />
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <MinMaxFilterInput
                label="Salary range"
                filterOn={filterJobsOn}
                setFilterOn={setFilterJobsOn}
                minKey="min_salary"
                maxKey="max_salary"
                handleClearFilterKey={handleClearFilterKey}
              />
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <FilterHeader
                title={"Location"}
                handleClearFilterKey={handleClearFilterKey}
                values={filterJobsOn}
                valueKey={[
                  "work_preference_new__selected",
                  "city_location_new__selected",
                ]}
              />
              <CheckboxInput
                values={filterJobsOn}
                setReactState={setFilterJobsOn}
                options={locationPreferenceOptions}
                valueKey="work_preference_new__selected"
              />

              {(filterJobsOn.work_preference_new__selected?.includes(
                locationPreferenceValues.Onsite,
              ) ||
                filterJobsOn.work_preference_new__selected?.includes(
                  locationPreferenceValues.Hybrid,
                )) && (
                <>
                  <div className="mt-tsm"></div>
                  <DataSelect
                    apiCall={citySearchApiCall}
                    values={filterJobsOn}
                    valueKey={"city_location_new__selected"}
                    setReactState={setFilterJobsOn}
                    isMulti
                    {...propToFormatCityLabelWithRhSelect}
                    getChipLabel={undefined}
                  />
                </>
              )}
            </div>

            <div className="mb-tmini">
              <RhDivider />
            </div>

            <div className="px-tsm py-tpico">
              <FilterHeader
                title={"Recruiters"}
                handleClearFilterKey={handleClearFilterKey}
                values={filterJobsOn}
                valueKey={["owner__selected"]}
              />
              <CheckboxInput
                values={filterJobsOn}
                setReactState={setFilterJobsOn}
                options={recruiterList}
                valueKey="owner__selected"
                limitOptionsOnRenderTo={5}
              />
            </div>
          </RhScrollbar>
        </div>
      </RhCard>
    </>
  );
};

export default JobFilter;
